import type { InjectionKey } from "vue";
import type { AppConfig } from "../app_config_provider";
import { ApiValidationError } from "./api_validation_error";

const API_PATH = "/v4/order";

export interface ProductVariantDto {
  id: number;
  group_id: number;
  product_id: number;
  product_position: number;
  product_title: string;
  product_type: string;
  product_allow_multiple: boolean;
  product_allow_multiple_variants: boolean;
  product_is_main: boolean;
  product_allow_qty_price: boolean;
  product_split_qty_variants: boolean;
  price_fix: number;
  price_rec: number;
  price_fix_incl: number;
  price_rec_incl: number;
  match_code: string;
  abo: string;
  title_cart: string;
  description: string;
  features: string;
  tags: string[];
  pos: number;
  image_url: string;
  mail_subject: string;
  qty_price_for: number | null;
  qty_min: number;
  qty_max: number | null;
  is_bestseller: boolean;
  promotext: string;
  promotext_new: string;
  terms: string;
  terms_label: string;
  numbers_included: number;
  numbers_max: number;
  channels_included: number;
  channels_max: number;
  info_category_type: string;
  info_category_uid: string;
}

export interface AvailableProductVariantRequest {
  netz_id: number | null;
  service_provider: string | null;
  service_type: string | null;
  service_area_code: string | null;
  customer_number_iway: number | null;
  order_for: string | null;
  productVariantIdsInCart: number[];
}
export interface OrderContactDto {
  contact_type: string;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  gender: string; // default 'n'
  street: string; // default ''
  building_number: string | null;
  zip: string; // default ''
  city: string; // default ''
  country_code: string; // default ''
  language: string; // default ''
  uid: string | null;
  email: string; // default ''
  phone: string; // default ''
  fax: string; // default ''
  mobile: string; // default ''
  birthdate: string | null;
}
export interface OrderContactPostRequest {
  contact_type: string | null;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  gender: string | null;
  street: string | null;
  building_number: string | null;
  zip: string | null;
  city: string | null;
  country_code: string | null;
  language: string | null;
  uid: string | null;
  email: string | null;
  phone: string | null;
  fax: string | null;
  mobile: string | null;
  birthdate: string | null;
}
export interface OrderItemDto {
  id: number | null;
  product_id: number;
  product_variant_id: number;
  qty: number;
  reference: string;
  price_fix: number;
  price_rec: number;
  title: string;
}

export interface ConfigDto {
  uuid: string;
  type: string;
  reference: string;
  data: any;
}
export interface OrderDto {
  version: number;
  netz_id: number;
  netz_slug: string;
  order_for: string;
  business_type: string;
  vat_type: string;
  tracking_reference: string;
  order_reference: string;
  ticket_id: number;
  partner_number: number;
  customer_number: number;
  summary: string;

  invoice_address: OrderContactDto | null;
  delivery_address: OrderContactDto | null;
  technical_address: OrderContactDto | null;

  ip: string;
  cart_source_id: number;

  terms: string | null;
  terms_label: string | null;
  user_name: string | null;

  ordered: string | null;
  confirmed: string | null;
  completed: string | null;
  canceled: string | null;
  created: string | null;
  updated: string | null;
  access_context: AccessContextDto|null;
  tenant_context: TenantContextDto|null;
  items: OrderItemDto[]; // from @var annotation
  configs: ConfigDto[]; // from @var annotation
}
export interface AccessContextDto {
  checker_result_uid: string | null;
  service_provider: string | null;
  service_type:   string | null;
  parent_area:  string | null;
  address_label:  string | null;
  max_downstream:  number | null;
  max_upstream:  number | null;
  has_copper:  boolean;
  oto_ids:  string[];
  first_activation_date:  string | null;
  checker_version:  number;
}

export interface TenantContextDto {
  addresss_invoice_business_only: boolean;
  ask_cart_source: boolean;
  cart_source_required: boolean;
  billing_period_choices: string[];
  billing_period_default: string;
  billing_type_choices: string[];
  billing_type_default: string;
  billing_type_ebill_available: boolean;
  billing_type_post_cost: number;
  enable_quote_link: boolean;
  enable_quote_pdf: boolean;
  wishdate_offset_days: number;
  wishdate_default: string;
  wishdate_choices: string[];
  wishdate_excluded: string[];
  show_minimum_contract_period_12M: boolean;

}

export interface OrderContextDto {
  addresss_invoice_business_only: boolean;
  ask_cart_source: boolean;
  cart_source_required: boolean;
  billing_type_choices: string[];
  billing_type_default: string;
  billing_type_ebill_available: boolean;
  billing_type_post_cost: number;
  enable_quote_link: boolean;
  enable_quote_pdf: boolean;
  wishdate_offset_days: number;
  wishdate_default: string;
  wishdate_choices: string[];
  wishdate_excluded: string[];
  show_minimum_contract_period_12M: boolean;
  confirmation_next_steps: string;
  confirmation_text: string;
  sources: OrderSourceDto[];
}

export interface OrderSourceDto {
  id: number;
  pos: number;
  title: string;
  is_default: boolean;
  info_category_uid: string;
}

export interface OrderCreateRequest {
  netz: string; // netz_id oder slug
  order_for: string;
  username: string | null;
  customer_number: number | null;
  partner_number: number | null;
  pv_ids: number[];
  checker_result_uid: string | null;
}

export interface OrderResetRequest {
  netz: string | null; // netz_id oder slug
  order_for: string | null;
  username: string | null;
  customer_number: number | null;
  partner_number: number | null;
  pv_ids: number[];
  checker_result_uid: string | null;
}
export interface OrderPatchRequest {
  billing_type?: string
  billing_period?: string
  source_id?: number
}

export interface AccessDslConfigPatchRequest {
  wishdate?: string | null;
  wishdate_type?: string | null;
  dsl_order_type?: string | null;
  dsl_number?: string | null;
  dsl_tenant?: string | null;
}
export interface AccessFiberConfigPatchRequest {
  wishdate?: string | null;
  wishdate_type?: string | null;
  oto_id_source?: string | null;
  oto_id?: string | null;
}

export interface AccessFixIpRipeConfigPatchRequest {
  ripe_handle?: string | null;
  ripe_reason?: string | null;
}

export interface LandlineNewConfigPatchRequest {
  number_prefix?: string | null;
  wishdate?: string | null;
  wishdate_type?: string | null;
}

export interface LandlinePortingConfigPatchRequest {
  landline_number?: string | null;
  wishdate?: string | null;
  wishdate_type?: string | null;
}
export interface TvConfigPatchRequest {
  wishdate?: string | null;
  wishdate_type?: string | null;
}

export interface MobileNewConfigPatchRequest {
  mobile_number?: string | null;
  owner_address_type?: string | null;
  owner_address?: OrderContactDto | null;
  legal_address_type?: string | null;
  legal_address?: OrderContactDto | null;
  wishdate?: string | null;
  wishdate_type?: string | null;
}

export interface MobilePortingConfigPatchRequest {
  mobile_number?: string | null;
  porting_type?: string | null;
  porting_date?: string | null;
  porting_time?: string | null;
  previous_provider?: string | null;
  previous_subscription_type?: string | null;
}

export interface AddToCartRequest {
  product_variant_id: number;
  reference: string;
  quantity: number;

}
export interface RemoveFromCartRequest {
  product_variant_id: number;
  reference: string;
  quantity: number;
}

export interface OrderChangedResponse {
  order: OrderDto;
  available_variants: ProductVariantDto[];
}

export interface VoucherCodeAddRequest {
  voucher_code: string | null;
}

export interface ReferrerCodeAddRequest {
  referrer_code: string | null;
}

export class OrderApi {
  private readonly config: Pick<AppConfig, "api_url" | "api_token">;

  constructor(config: Pick<AppConfig, "api_url" | "api_token">) {
    this.config = config;
  }

  async getAvailableVariants(order_reference: string): Promise<ProductVariantDto[]> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/available_variants`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
      },
    );

    if (!response.ok) {
      console.log(
        `getAvailableProducts failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<ProductVariantDto[]>;
  }

  async getOrderByReference(order_reference: string): Promise<OrderDto | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/view`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
      },
    );

    if (!response.ok) {
      if (response.status == 404){
        return null;
      }
      console.log(
        `getOrderByReference failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderDto | null>;
  }

  async getOrderContextByReference(order_reference: string): Promise<OrderContextDto | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/context`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
      },
    );

    if (!response.ok) {
      if (response.status == 404){
        return null;
      }
      console.log(
        `getOrderByReference failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderContextDto | null>;
  }

  async create(request: OrderCreateRequest): Promise<OrderDto | null> {
    const response = await fetch(`${this.config.api_url}${API_PATH}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-TOKEN": this.config.api_token,
      },
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `orderCreateRequest failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderDto | null>;
  }
  async reset(
    order_reference: string,
    request: OrderResetRequest,
  ): Promise<OrderDto | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/reset`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `orderCreateRequest failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderDto | null>;
  }

  async updateOrder(
    order_reference: string,
    request: OrderPatchRequest,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/update`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      console.log(
        `orderPatchRequest failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderChangedResponse | null>;
  }

  async addToCart(
    order_reference: string,
    request: AddToCartRequest,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/add_to_cart`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `orderCreateRequest failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderChangedResponse | null>;
  }

  async removeFromCart(
    order_reference: string,
    request: RemoveFromCartRequest,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/remove_from_cart`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `orderCreateRequest failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderChangedResponse | null>;
  }


  async patchConfig(
    order_reference: string,
    config_type: string,
    config_id: string,
    request: object,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/config/${config_type}/${config_id}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `${config_type} patch failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderChangedResponse | null>;
  }

  async postAddress(
    order_reference: string,
    address_type: string,
    request: OrderContactPostRequest | null,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/address/${address_type}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `address post failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderChangedResponse | null>;
  }


  async addVoucher(
    order_reference: string,
    request: VoucherCodeAddRequest,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/add_voucher`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `add_voucher failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderChangedResponse | null>;
  }

  async addReferrer(
    order_reference: string,
    request: ReferrerCodeAddRequest,
  ): Promise<OrderChangedResponse | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/add_referrer`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `add_referrer failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderChangedResponse | null>;
  }

  async submit(
    order_reference: string,
  ): Promise<OrderDto | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/submit`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
      },
    );

    if (!response.ok) {
      if (response.status == 422) {
        const body = await response.json();
        throw new ApiValidationError(body);
      }
      console.log(
        `order submit failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }
    return response.json() as Promise<OrderDto | null>;
  }

  async import(
    order_reference: string,
  ): Promise<OrderDto | null> {
    const response = await fetch(
      `${this.config.api_url}${API_PATH}/${order_reference}/import`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-TOKEN": this.config.api_token,
        },
      },
    );

    if (!response.ok) {
      console.log(
        `orderImport failed: ${response.status} ${response.statusText}`,
      );
      throw new Error("General_Api_Failed");
    }

    return response.json() as Promise<OrderDto | null>;
  }
}

export const orderApiKey: InjectionKey<OrderApi> = Symbol("order-api");
